

export interface RealtimeConnectOptions {
  userName: string;
  token: string;
}

export type RealtimeConnectionState =
  | "disconnected"
  | "connecting"
  | "connected"
  | "authenticated"
  | "ready"
  | "reconnecting";


/* ============================================================
 * COMMON TYPES
 * ============================================================ */

export type RealtimeCameraKey =
  | "front"
  | "cabin"
  | string;

export type RealtimeStreamType =
  | "main"
  | "sub"
  | string;


/* ============================================================
 * AUTHENTICATION
 * ============================================================ */

export interface RealtimeAuthenticateMessage {
  type: "authenticate";
  userName: string;
  token: string;
}

export interface RealtimeAuthenticatedMessage {
  type: "authenticated";
}


/* ============================================================
 * READY
 *
 * Backend trả danh sách IMEI mà user được phép xem.
 * ============================================================ */

export interface RealtimeReadyMessage {
  type: "ready";
  userName: string;
  devices: string[];
}

export interface RealtimeDeviceOnlineChangedMessage {
  type: "DeviceOnlineChanged";
  payload: {
    imei: string;
    online: boolean;
  };
}


/* ============================================================
 * POSITION
 * ============================================================ */

export interface RealtimePositionUpdatedMessage {
  type: "PositionUpdated";

  payload: RealtimePositionPayload;

  heading: number | null;

  latitude: number;
  longitude: number;

  speedKmh: number;

  recordedAt: string;
}

export interface RealtimePositionPayload {
  imei: string;
  latitude: number;
  longitude: number;
  heading: number | null;
  speedKmh: number | null;
  recordedAt: string;
}


/* ============================================================
 * CAMERA STREAM
 * ============================================================ */

export interface RealtimeCameraStreamChangedMessage {
  type: "CameraStreamChanged";

  payload: {
    imei: string;
    cameraKey: RealtimeCameraKey;
    streamType: RealtimeStreamType;
    isStreaming: boolean;
    path: string | null;
  };
}


/* ============================================================
 * PLAYBACK DAYS
 * ============================================================ */

export interface RealtimePlaybackDaysUpdatedMessage {
  type: "PlaybackDaysUpdated";

  payload: {
    imei: string;
    cameraKey: RealtimeCameraKey;
    days: string[];
  };
}


/* ============================================================
 * PLAYBACK TIMELINE
 * ============================================================ */

export interface RealtimeRecordingRange {
  from: string;
  to: string;
}

export interface RealtimePlaybackTimelineUpdatedMessage {
  type: "PlaybackTimelineUpdated";
  payload: {
    imei: string;
    cameraKey: RealtimeCameraKey;
    date: string;
    ranges: RealtimeRecordingRange[];
  };
}


/* ============================================================
 * PLAYBACK POSITION
 * ============================================================ */

export interface RealtimePlaybackPositionUpdatedMessage {
  type: "PlaybackPositionUpdated";

  payload: {
    imei: string;
    cameraKey: RealtimeCameraKey;
    latitude: number;
    longitude: number;
    speedKmh: number | null;
    recordedAt: string;
  };
}


/* ============================================================
 * ERROR
 * ============================================================ */

export interface RealtimeErrorMessage {
  type: "error";

  message: string;

  code?: string;
}


/* ============================================================
 * SERVER MESSAGE UNION
 * ============================================================ */

export type RealtimeServerMessage =
  | RealtimeAuthenticatedMessage
  | RealtimeReadyMessage
  | RealtimeDeviceOnlineChangedMessage
  | RealtimePositionUpdatedMessage
  | RealtimeCameraStreamChangedMessage
  | RealtimePlaybackDaysUpdatedMessage
  | RealtimePlaybackTimelineUpdatedMessage
  | RealtimePlaybackPositionUpdatedMessage
  | RealtimeErrorMessage;


/* ============================================================
 * CLIENT MESSAGE UNION
 * ============================================================ */

export type RealtimeClientMessage =
  | RealtimeAuthenticateMessage;


/* ============================================================
 * MESSAGE HANDLER
 * ============================================================ */

export type RealtimeMessageHandler = (
  message: RealtimeServerMessage,
) => void;


/* ============================================================
 * UNKNOWN HELPERS
 * ============================================================ */

function isRecord(
  value: unknown,
): value is Record<string, unknown> {
  return (
    typeof value === "object" &&
    value !== null
  );
}

function isString(
  value: unknown,
): value is string {
  return typeof value === "string";
}

function isNumber(
  value: unknown,
): value is number {
  return (
    typeof value === "number" &&
    Number.isFinite(value)
  );
}

function isBoolean(
  value: unknown,
): value is boolean {
  return typeof value === "boolean";
}

function isStringArray(
  value: unknown,
): value is string[] {
  return (
    Array.isArray(value) &&
    value.every(
      (item: unknown) =>
        typeof item === "string",
    )
  );
}


/* ============================================================
 * RECORDING RANGE GUARD
 * ============================================================ */

function isRealtimeRecordingRange(
  value: unknown,
): value is RealtimeRecordingRange {
  if (!isRecord(value)) {
    return false;
  }

  return (
    isString(value.from) &&
    isString(value.to)
  );
}


/* ============================================================
 * AUTHENTICATED GUARD
 *
 * IMPORTANT:
 * Parameter is UNKNOWN.
 *
 * This avoids:
 *
 * A type predicate's type must be assignable...
 * ============================================================ */

function isRealtimeAuthenticatedMessage(
  value: unknown,
): value is RealtimeAuthenticatedMessage {
  if (!isRecord(value)) {
    return false;
  }

  return value.type === "authenticated";
}


/* ============================================================
 * READY GUARD
 * ============================================================ */

function isRealtimeReadyMessage(
  value: unknown,
): value is RealtimeReadyMessage {
  if (!isRecord(value)) {
    return false;
  }

  return (
    value.type === "ready" &&
    isString(value.userName) &&
    isStringArray(value.devices)
  );
}

function isRealtimeDeviceOnlineChangedMessage(
  value: unknown,
): value is RealtimeDeviceOnlineChangedMessage {
  if (!isRecord(value) || !isRecord(value.payload)) {
    return false;
  }

  return (
    value.type === "DeviceOnlineChanged" &&
    isString(value.payload.imei) &&
    isBoolean(value.payload.online)
  );
}


/* ============================================================
 * POSITION GUARD
 * ============================================================ */

function isRealtimePositionUpdatedMessage(
  value: unknown,
): value is RealtimePositionUpdatedMessage {
  if (!isRecord(value)) {
    return false;
  }

  const payload = value.payload;

  if (!isRecord(payload)) {
    return false;
  }

  return (
    value.type === "PositionUpdated" &&
    isString(payload.imei) &&
    isNumber(payload.latitude) &&
    isNumber(payload.longitude) &&
    (payload.heading === null || isNumber(payload.heading)) &&
    (payload.speedKmh === null || isNumber(payload.speedKmh)) &&
    isString(payload.recordedAt)
  );
}


/* ============================================================
 * CAMERA STREAM GUARD
 * ============================================================ */

function isRealtimeCameraStreamChangedMessage(
  value: unknown,
): value is RealtimeCameraStreamChangedMessage {
  if (!isRecord(value)) {
    return false;
  }

  if (
    value.type !== "CameraStreamChanged" ||
    !isRecord(value.payload)
  ) {
    return false;
  }

  return (
    isString(value.payload.imei) &&
    isString(value.payload.cameraKey) &&
    isString(value.payload.streamType) &&
    isBoolean(value.payload.isStreaming) &&
    (
      value.payload.path === null ||
      isString(value.payload.path)
    )
  );
}


/* ============================================================
 * PLAYBACK DAYS GUARD
 * ============================================================ */

function isRealtimePlaybackDaysUpdatedMessage(
  value: unknown,
): value is RealtimePlaybackDaysUpdatedMessage {
  if (!isRecord(value)) {
    return false;
  }

  if (
    value.type !== "PlaybackDaysUpdated" ||
    !isRecord(value.payload)
  ) {
    return false;
  }

  return (
    isString(value.payload.imei) &&
    isString(value.payload.cameraKey) &&
    isStringArray(value.payload.days)
  );
}


/* ============================================================
 * PLAYBACK TIMELINE GUARD
 * ============================================================ */

function isRealtimePlaybackTimelineUpdatedMessage(
  value: unknown,
): value is RealtimePlaybackTimelineUpdatedMessage {
  if (
    !isRecord(value) ||
    value.type !== "PlaybackTimelineUpdated" ||
    !isRecord(value.payload)
  ) {
    return false;
  }

  return (
    isString(value.payload.imei) &&
    isString(value.payload.cameraKey) &&
    isString(value.payload.date) &&
    Array.isArray(value.payload.ranges) &&
    value.payload.ranges.every(
      (range: unknown) =>
        isRealtimeRecordingRange(range),
    )
  );
}


/* ============================================================
 * PLAYBACK POSITION GUARD
 * ============================================================ */

function isRealtimePlaybackPositionUpdatedMessage(
  value: unknown,
): value is RealtimePlaybackPositionUpdatedMessage {
  if (
    !isRecord(value) ||
    value.type !== "PlaybackPositionUpdated" ||
    !isRecord(value.payload)
  ) {
    return false;
  }

  return (
    isString(value.payload.imei) &&
    isString(value.payload.cameraKey) &&
    isNumber(value.payload.latitude) &&
    isNumber(value.payload.longitude) &&
    (
      value.payload.speedKmh === null ||
      isNumber(value.payload.speedKmh)
    ) &&
    isString(value.payload.recordedAt)
  );
}


/* ============================================================
 * ERROR GUARD
 * ============================================================ */

function isRealtimeErrorMessage(
  value: unknown,
): value is RealtimeErrorMessage {
  if (!isRecord(value)) {
    return false;
  }

  return (
    value.type === "error" &&
    isString(value.message) &&
    (
      value.code === undefined ||
      isString(value.code)
    )
  );
}


/* ============================================================
 * MAIN SERVER MESSAGE GUARD
 * ============================================================ */

function isRealtimeServerMessage(
  value: unknown,
): value is RealtimeServerMessage {
  return (
    isRealtimeAuthenticatedMessage(value) ||
    isRealtimeReadyMessage(value) ||
    isRealtimeDeviceOnlineChangedMessage(value) ||
    isRealtimePositionUpdatedMessage(value) ||
    isRealtimeCameraStreamChangedMessage(value) ||
    isRealtimePlaybackDaysUpdatedMessage(value) ||
    isRealtimePlaybackTimelineUpdatedMessage(value) ||
    isRealtimePlaybackPositionUpdatedMessage(value) ||
    isRealtimeErrorMessage(value)
  );
}


/* ============================================================
 * REALTIME MANAGER
 * ============================================================ */

class RealtimeManager {
  private socket: WebSocket | null = null;

  private state: RealtimeConnectionState =
    "disconnected";

  private connectPromise:
    Promise<void> | null = null;

  private messageHandlers =
    new Set<RealtimeMessageHandler>();

  private currentUserName:
    string | null = null;

  private currentToken:
    string | null = null;


  /* ==========================================================
   * CONNECT
   * ========================================================== */

  async connect(
    options: RealtimeConnectOptions,
  ): Promise<void> {
    if (
      typeof window === "undefined"
    ) {
      return;
    }

    /*
     * Same user is already ready.
     */
    if (
      this.state === "ready" &&
      this.currentUserName ===
        options.userName
    ) {
      return;
    }

    /*
     * Connection is already being established.
     */
    if (this.connectPromise) {
      return this.connectPromise;
    }

    this.connectPromise =
      this.createConnection(options);

    try {
      await this.connectPromise;
    } finally {
      this.connectPromise = null;
    }
  }


  /* ==========================================================
   * CREATE CONNECTION
   * ========================================================== */

  private async createConnection(
    options: RealtimeConnectOptions,
  ): Promise<void> {
    await this.close();

    this.state = "connecting";

    this.currentUserName =
      options.userName;

    this.currentToken =
      options.token;

    const wsUrl =
      this.getWebSocketUrl();

    await new Promise<void>(
      (resolve, reject) => {
        const socket =
          new WebSocket(wsUrl);

        this.socket = socket;

        let settled = false;


        /* ------------------------------------------------------
         * Mark promise settled
         * ------------------------------------------------------ */

        const markSettled =
          (): void => {
            settled = true;
          };


        /* ------------------------------------------------------
         * Fail
         * ------------------------------------------------------ */

        const fail = (
          error: Error,
        ): void => {
          if (settled) {
            return;
          }

          settled = true;

          reject(error);
        };


        /* ------------------------------------------------------
         * OPEN
         * ------------------------------------------------------ */

        socket.onopen = (): void => {
          console.log(
            "[REALTIME] WebSocket connected",
          );

          this.state =
            "connected";

          const authenticateMessage:
            RealtimeAuthenticateMessage = {
              type: "authenticate",
              userName:
                options.userName,
              token:
                options.token,
            };

          const message:
            RealtimeClientMessage =
            authenticateMessage;

          socket.send(
            JSON.stringify(message),
          );

          console.log(
            "[REALTIME] Authentication sent",
            {
              userName:
                options.userName,
            },
          );
        };


        /* ------------------------------------------------------
         * MESSAGE
         * ------------------------------------------------------ */

        socket.onmessage = (
          event: MessageEvent,
        ): void => {
          if (
            typeof event.data !==
            "string"
          ) {
            console.error(
              "[REALTIME] Unsupported WebSocket message data",
            );

            return;
          }

          this.handleMessage(
            event.data,
            resolve,
            fail,
            markSettled,
          );
        };


        /* ------------------------------------------------------
         * ERROR
         * ------------------------------------------------------ */

        socket.onerror = (): void => {
          console.error(
            "[REALTIME] WebSocket error",
          );

          fail(
            new Error(
              "Realtime WebSocket connection failed.",
            ),
          );
        };


        /* ------------------------------------------------------
         * CLOSE
         * ------------------------------------------------------ */

        socket.onclose = (
          event: CloseEvent,
        ): void => {
          console.log(
            "[REALTIME] WebSocket closed",
            {
              code: event.code,
              reason: event.reason,
            },
          );

          this.state =
            "disconnected";

          this.socket = null;

          if (!settled) {
            fail(
              new Error(
                "Realtime WebSocket closed before ready.",
              ),
            );
          }
        };
      },
    );
  }


  private handleMessage( rawMessage: string, resolve: () => void, reject: (error: Error) => void, markSettled: () => void, ): void 
  {
    let parsed: unknown;

    try {
      parsed = JSON.parse(rawMessage);
    } catch {
      console.error( "[REALTIME] Invalid JSON message", rawMessage, );

      return;
    }

    if ( !isRealtimeServerMessage(parsed, )) 
    {
      console.error("[REALTIME] Unknown realtime message",parsed,);
      return;
    }

    const message = parsed;
    console.log( "[REALTIME] Message", message, );

    if (message.type === "authenticated") 
    {
      this.state = "authenticated";
      this.emit(message);
      return;
    }

    if (message.type === "ready") 
    {
      this.state = "ready";
      markSettled();
      console.log("[REALTIME] Ready",{userName:message.userName,devices:message.devices,},);
      this.emit(message);
      resolve();
      return;
    }


    if (message.type ==="error") 
    {
      markSettled();
      const error = new Error(message.message,);
      console.error("[REALTIME] Server error",{message:message.message,code:message.code,},);
      this.emit(message);
      reject(error);
      return;
    }

    this.emit(message);
  }


  /* ==========================================================
   * GET WEBSOCKET URL
   * ========================================================== */

  private getWebSocketUrl(): string {
    const configuredUrl =
      process.env
        .NEXT_PUBLIC_REALTIME_WS_URL;

    if (
      configuredUrl &&
      configuredUrl.trim() !== ""
    ) {
      return configuredUrl.trim();
    }

    if (
      typeof window !== "undefined"
    ) {
      const protocol =
        window.location.protocol ===
        "https:"
          ? "wss:"
          : "ws:";

      return (
        `${protocol}//` +
        `${window.location.host}` +
        `/ws/realtime`
      );
    }

    throw new Error(
      "Realtime WebSocket URL is not configured.",
    );
  }


  /* ==========================================================
   * REGISTER MESSAGE HANDLER
   * ========================================================== */

  on(
    handler: RealtimeMessageHandler,
  ): () => void {
    this.messageHandlers.add(
      handler,
    );

    return () => {
      this.messageHandlers.delete(
        handler,
      );
    };
  }


  /* ==========================================================
   * EMIT
   * ========================================================== */

  private emit(
    message: RealtimeServerMessage,
  ): void {
    for (
      const handler of
        this.messageHandlers
    ) {
      try {
        handler(message);
      } catch (
        error: unknown
      ) {
        console.error(
          "[REALTIME] Message handler failed",
          error,
        );
      }
    }
  }


  /* ==========================================================
   * STATE
   * ========================================================== */

  getState():
    RealtimeConnectionState {
    return this.state;
  }


  /* ==========================================================
   * READY
   * ========================================================== */

  isReady(): boolean {
    return (
      this.state === "ready"
    );
  }


  /* ==========================================================
   * CURRENT USER
   * ========================================================== */

  getCurrentUserName():
    string | null {
    return this.currentUserName;
  }


  /* ==========================================================
   * CLOSE
   * ========================================================== */

  async close(): Promise<void> {
    const socket =
      this.socket;

    this.socket = null;

    if (!socket) {
      this.state =
        "disconnected";

      return;
    }

    if (
      socket.readyState ===
      WebSocket.OPEN
    ) {
      socket.close(
        1000,
        "Client logout",
      );
    }

    this.state =
      "disconnected";
  }


  /* ==========================================================
   * DISCONNECT
   * ========================================================== */

  async disconnect(): Promise<void> {
    await this.close();

    this.currentUserName =
      null;

    this.currentToken =
      null;
  }
}


/* ============================================================
 * SINGLETON
 *
 * Chỉ có MỘT instance trong toàn bộ frontend runtime.
 * ============================================================ */

export const realtimeManager =  new RealtimeManager();
